/*
 * Composable för stora postlistor i importpanelerna:
 *  - årsfilter (chips med antal)
 *  - gruppering per månad (nyast först)
 *  - fönstrad rendering: bara `limit` rader i DOM, fler laddas via knapp eller
 *    när en sentinel-div blir synlig (IntersectionObserver)
 *
 * `source` är en computed/ref med redan status-filtrerade poster. Poster läses
 * på `startedAt || date`.
 */
import { ref, computed, watch, onBeforeUnmount } from 'vue';

const PAGE = 80;
const MONTHS = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

export const itemTime = it => new Date(it.startedAt || it.date).getTime();

export function useTimeline(source) {
  const year = ref('all');
  const limit = ref(PAGE);
  const sentinel = ref(null);

  // Årschips med antal (från den status-filtrerade listan).
  const years = computed(() => {
    const m = new Map();
    for (const it of source.value) {
      const y = new Date(it.startedAt || it.date).getFullYear();
      if (!isNaN(y)) m.set(y, (m.get(y) || 0) + 1);
    }
    return [...m.entries()].sort((a, b) => b[0] - a[0]).map(([y, count]) => ({ year: String(y), count }));
  });

  // Sorterad (nyast först) och årsfiltrerad lista.
  const filtered = computed(() => {
    const y = year.value;
    const arr = y === 'all' ? [...source.value] : source.value.filter(it => new Date(it.startedAt || it.date).getFullYear() === +y);
    arr.sort((a, b) => itemTime(b) - itemTime(a));
    return arr;
  });

  // Alla poster per månad (för månadsradens bulk-knappar), byggs i ett svep.
  const byMonth = computed(() => {
    const m = new Map();
    for (const it of filtered.value) {
      const k = monthKey(it);
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(it);
    }
    return m;
  });

  // Fönstrade månadsgrupper: bara de första `limit` raderna hamnar i DOM.
  const groups = computed(() => {
    const out = [];
    let rendered = 0, cur = null;
    for (const it of filtered.value) {
      if (rendered >= limit.value) break;
      const key = monthKey(it);
      if (!cur || cur.key !== key) {
        const d = new Date(it.startedAt || it.date);
        cur = { key, label: `${MONTHS[d.getMonth()]} ${d.getFullYear()}`, items: [], all: byMonth.value.get(key) || [] };
        out.push(cur);
      }
      cur.items.push(it);
      rendered++;
    }
    return out;
  });

  const remaining = computed(() => Math.max(0, filtered.value.length - limit.value));
  function showMore() { limit.value += PAGE * 2; }
  function resetWindow() { limit.value = PAGE; }
  watch([year, () => source.value], resetWindow);

  // Autoladda när sentinel syns.
  let observer = null;
  watch(sentinel, el => {
    observer?.disconnect();
    observer = null;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    observer = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting) && remaining.value > 0) showMore();
    }, { rootMargin: '400px' });
    observer.observe(el);
  });
  onBeforeUnmount(() => observer?.disconnect());

  return { year, years, filtered, groups, remaining, showMore, sentinel };
}

function monthKey(it) {
  const d = new Date(it.startedAt || it.date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
